import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
// 0.36.293 — Admin-configurable platform footer (bg, text, colors,
// Hightech-standards link, logos, top strokes).
import { useLayoutConfig } from '../context/LayoutConfigContext';
import MarkdownContent from './MarkdownContent';
import styles from './Footer.module.css';
// 0.39.63 - Resolve =lib() page titles in footer links.
import { resolveTitleText } from '../lib/resolveTitleText';
import { colorCss } from '../lib/colorCss';

/**
 * Public footer.
 *
 * Layout:
 *   - Optional "Link to Hightech standards" line (icon + markdown
 *     link) at the top when enabled.
 *   - Link row, dynamic, from pages with show_in_footer=true.
 *   - Logos: admin-uploaded data URLs (legacy single logo migrated in).
 *   - Background / text colors: applied as inline style overrides on the
 *     root <footer> when admin set them; '' falls through to the CSS
 *     module defaults.
 */
export default function Footer() {
  const { language } = useLanguage();
  const { footer: footerConfig, loading: layoutLoading } = useLayoutConfig();
  // 0.44.06 - The footer no longer lists "Show in footer" pages: that
  // setting is gone (the site's menu is edited in Pages and forms).

  // 0.39.05 - Don't render the footer until the layout config has
  // loaded. Before this, footerConfig was null on first paint, so the
  // footer showed the CSS-default dark-blue background and the old
  // hardcoded fallback body text for a moment, then snapped to the
  // admin-configured colour + body once the config arrived. Holding
  // render until loading completes removes that flash entirely. The
  // footer sits at the very bottom of the page, so nothing above it
  // is delayed - only the footer band waits.
  if (layoutLoading) return null;

  const cfg = footerConfig || {};
  const rootStyle = (() => {
    const s = {};
    if (colorCss(cfg.bg_color))   s.background = colorCss(cfg.bg_color);
    if (colorCss(cfg.text_color)) s.color = colorCss(cfg.text_color);
    // 0.36.724 — Admin-configurable footer height. Falls through
    // to the CSS default (styles.footer natural height) when 0 /
    // unset. Applied as min-height so content taller than the
    // set value still fits without clipping.
    const fh = Number(cfg.footer_height) || 0;
    if (fh > 0) s.minHeight = `${fh}px`;
    // 0.39.75 - The top-stroke band is absolutely positioned over the
    // footer's top padding, so a thick stroke ate into the 24px gap
    // (e.g. a 10px stroke left only ~14px between it and the standards
    // link). Add the total stroke height to the top padding so the
    // VISIBLE gap from the bottom of the strokes to the content is
    // always 24px, regardless of stroke thickness.
    const strokeTotal = (Array.isArray(cfg.top_strokes) ? cfg.top_strokes : [])
      .reduce((sum, st) => sum + (Number(st && st.width) || 0), 0);
    if (strokeTotal > 0) {
      // 32px (var(--space-8)) base gap + the strokes' height.
      s.paddingTop = `calc(var(--space-8) + ${strokeTotal}px)`;
    }
    return Object.keys(s).length ? s : undefined;
  })();
  const logoSrc = cfg.logo_data_url || '';

  // 0.38.92 - Admin-configurable top strokes. `top_strokes` is an
  // array of { width, color } objects rendered as full-width bands
  // stacked at the very top of the footer, in order (first entry is
  // the topmost). width is px; color is a --token string (resolved
  // via var()) or '' for the default text color. Empty / missing
  // array -> no strokes (back-compat with footers saved before this).
  const topStrokes = Array.isArray(cfg.top_strokes) ? cfg.top_strokes : [];

  // 0.38.93 - Logos. New `logos` array (each a data URL) supersedes
  // the single logo_data_url. Back-compat: if `logos` is empty but
  // logo_data_url is set, treat that single logo as the list. All
  // logos render at a fixed 90px height with a 24px horizontal gap,
  // in the right column group. The right group wraps BELOW the left
  // content when there isn't room (flex-wrap on the container), and
  // is hidden entirely on narrow screens (CSS, see .logoColumn).
  // 0.39.04 - Logo resolution. `logos` (array of data URLs) is
  // authoritative once present. The legacy single `logo_data_url` is
  // ONLY a fallback when `logos` is entirely ABSENT (a footer saved
  // before multi-logo, never migrated). Previously an EMPTY logos
  // array also fell back to logo_data_url - so removing every logo in
  // the editor made the old single logo reappear on the public
  // footer. Now: logos present (even empty) -> use it verbatim;
  // logos absent -> fall back to the legacy single logo.
  const logoList = (() => {
    if (Array.isArray(cfg.logos)) {
      // Present (possibly empty) -> authoritative.
      return cfg.logos.filter(u => typeof u === 'string' && u);
    }
    // logos key absent -> legacy fallback.
    return logoSrc ? [logoSrc] : [];
  })();
  // 0.39.10 - Admin footer logo height (px). 0 / unset -> CSS default.
  const logoHeight = Number(cfg.footer_logo_height) || 0;

  // 0.39.20 - hightech_link_text is per-language: { lang: text }.
  // Resolve for the citizen's current language, falling back to the
  // first non-empty value. A legacy plain string is used as-is.
  const hightechText = (() => {
    const t = cfg.hightech_link_text;
    if (typeof t === 'string') return t;
    if (t && typeof t === 'object') {
      if (t[language]) return t[language];
      const firstNonEmpty = Object.values(t).find(v => typeof v === 'string' && v);
      return firstNonEmpty || '';
    }
    return '';
  })();

  return (
    <footer className={styles.footer} style={rootStyle}>
      {topStrokes.length > 0 && (
        <div className={styles.topStrokes} aria-hidden="true">
          {topStrokes.map((s, i) => {
            const w = Number(s && s.width) || 0;
            if (w <= 0) return null;
            const bg = (s && colorCss(s.color)) || 'currentColor';
            return (
              <div key={i} className={styles.topStroke}
                   style={{ height: `${w}px`, background: bg }} />
            );
          })}
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.contentColumn}>
          {/* 0.39.09 - "Link to Hightech standards" line. The first
              content line when hightech_link_show is set. The text
              goes through MarkdownContent so
              [text](url){: hyperlink="ext"} renders as a proper
              external link (with the built-in external-link marker the
              IAL adds); plain text renders as plain text. An optional
              leading icon sits before it. Left-aligned; inherits the
              footer text color. */}
          {cfg.hightech_link_show && hightechText && (
            <div className={styles.hightechLink}>
              {cfg.hightech_link_icon && (
                /* 0.39.73 - Admin-uploaded icon image (data URL) at an
                   admin-adjustable height (hightech_link_icon_height,
                   default 14px); width auto to keep the aspect ratio. */
                <img src={cfg.hightech_link_icon} alt=""
                     className={styles.hightechLinkIcon}
                     style={{ height: `${Number(cfg.hightech_link_icon_height) || 14}px` }} />
              )}
              <MarkdownContent source={hightechText} />
            </div>
          )}
        </div>
        {logoList.length > 0 && (
          <div className={styles.logoColumn}>
            {logoList.map((src, i) => (
              <div key={i} className={styles.logoCard}>
                {/* 0.39.10 - Admin-adjustable logo height. footer_logo_height
                    (px) overrides the .logo CSS default (90px) when set
                    (> 0); 0 / unset falls through to the CSS default. */}
                <img src={src} alt="" className={styles.logo}
                     style={logoHeight > 0 ? { height: `${logoHeight}px` } : undefined} />
              </div>
            ))}
          </div>
        )}
      </div>
    </footer>
  );
}
